import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Shield } from 'lucide-react';
import Footer from '@/components/Footer';

const Privacy = () => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPrivacy = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/policies/privacy`);
        setContent(response.data.content);
      } catch (error) {
        console.error('Failed to load privacy policy:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPrivacy();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Custom styles to preserve HTML inline styles */}
      <style>{`
        .privacy-content * {
          all: revert;
        }
        .privacy-content {
          line-height: normal;
        }
        .privacy-content p {
          margin: 0 0 6px;
        }
        .privacy-content h2 {
          margin: 0 0 4px;
        }
        .privacy-content h3 {
          margin: 6px 0 4px;
        }
        .privacy-content ul {
          margin: 0 0 6px 1rem;
          padding: 0;
        }
        .privacy-content li {
          margin: 2px 0;
        }
        .privacy-content section {
          padding: 8px 0;
          border-bottom: 1px solid #eef0f4;
        }
      `}</style>
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </Link>
            <Link to="/login" className="text-purple-600 hover:text-purple-700 font-semibold">
              Login
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 md:p-12">
          {/* Title */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
                Privacy Policy
              </h1>
              <p className="text-sm text-gray-500 mt-1">Effective Date: November 2025</p>
            </div>
          </div>

          {/* Privacy Content */}
          <div className="max-w-none">
            {content ? (
              <div 
                className="privacy-content" 
                dangerouslySetInnerHTML={{ __html: content }}
              />
            ) : (
              <div className="whitespace-pre-wrap text-gray-700 leading-relaxed" style={{ fontFamily: 'system-ui' }}>
{`Privacy Policy

Effective Date: November 2025

Entity: Shootzu, owned and operated by Turbo Productions Pty Ltd (ABN 18 150 556 102)
Referred to as "we", "us" or "our" in this policy.

1. Introduction

We respect your privacy and are committed to protecting your personal information. This Privacy Policy outlines how we collect, use, store and disclose personal information in compliance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).

By using our platform, websites, applications or services, you consent to the terms of this Privacy Policy.

[Note: Full privacy policy content would be displayed here from the database]`}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Privacy;